import { memo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ThirdParty } from '../types';
import { formatDate } from '../utils/formatters';
import { ExpandChevron } from './ExpandChevron';
import { VendorLogo } from './VendorLogo';
import { RiskBadge } from './RiskBadge';
import { StatusLabel } from './StatusLabel';
import { BusinessCriticalityLabel } from './BusinessCriticalityLabel';

interface VendorRowProps {
  vendor: ThirdParty;
  isExpanded: boolean;
  onToggle: () => void;
}

export const VendorRow = memo(function VendorRow({
  vendor,
  isExpanded,
  onToggle,
}: VendorRowProps) {
  const navigate = useNavigate();

  const handleRowClick = useCallback(() => {
    navigate(`/third-party-risk/vendor/${encodeURIComponent(vendor.vendor_name)}`);
  }, [navigate, vendor.vendor_name]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        handleRowClick();
      }
    },
    [handleRowClick]
  );

  return (
    <div
      role="row"
      aria-expanded={isExpanded}
      aria-level={1}
      tabIndex={0}
      onClick={handleRowClick}
      onKeyDown={handleKeyDown}
      className="flex flex-row items-center px-3 pr-4 py-3 gap-3 h-11 bg-white border-b border-[#E2E8F0] cursor-pointer hover:bg-[#F8FAFC] transition-colors"
    >
      {/* Expand Chevron */}
      <div
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="cursor-pointer"
      >
        <ExpandChevron isExpanded={isExpanded} />
      </div>

      {/* Columns container */}
      <div className="flex flex-row items-center justify-between flex-1 gap-4">
        {/* Third-Party Column */}
        <div className="flex items-center gap-2 w-[240px]">
          <VendorLogo vendorName={vendor.vendor_name} logoUrl={vendor.vendor_logo_url} />
          <div className="flex items-baseline gap-1 min-w-0 flex-1">
            <span className="font-['Inter'] font-semibold text-sm leading-5 text-[#0F172A] truncate">
              {vendor.vendor_name}
            </span>
            <span
              onClick={(e) => {
                e.stopPropagation();
                onToggle();
              }}
              className="font-['Inter'] font-normal text-xs leading-4 text-[#62748E] flex-shrink-0 cursor-pointer hover:text-[#006FEE] transition-colors"
            >
              {vendor.number_of_assessments} assessment{vendor.number_of_assessments !== 1 ? 's' : ''}
            </span>
          </div>
        </div>

        {/* Risk Column */}
        <div className="flex items-center w-[160px]">
          <RiskBadge risk={vendor.risk_level} />
        </div>

        {/* Business Criticality Column */}
        <div className="flex items-center w-[180px]">
          <BusinessCriticalityLabel criticality={vendor.vendor_business_criticality} />
        </div>

        {/* Status Column */}
        <div className="flex items-center w-[160px]">
          {vendor.vendor_status ? (
            <StatusLabel
              status={vendor.vendor_status}
              count={vendor.vendor_status === 'needs_review' ? vendor.assessments_needs_review_count : undefined}
            />
          ) : (
            <span className="font-['Inter'] font-normal text-sm leading-5 text-[#94A3B8]">—</span>
          )}
        </div>

        {/* Last Assessed Column */}
        <div className="w-[160px]">
          <span className="font-['Inter'] font-normal text-sm leading-5 text-[#314158]">
            {formatDate(vendor.last_updated_at)}
          </span>
        </div>

        {/* Actions spacer - to align with ProductRow actions */}
        <div className="w-5 h-5 flex-shrink-0" />
      </div>
    </div>
  );
});
